<script setup lang="ts">
import { useLogout } from "@cms/features/Auth/Logout/useLogout";
import VIcon from "@verbleif/lib/src/components/Icon/VIcon.vue";
import MobileSheetGroup from "@verbleif/lib/src/components/MobileSheet/MobileSheetGroup.vue";
import MobileSheetRow from "@verbleif/lib/src/components/MobileSheet/MobileSheetRow.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useNavbarLocale } from "./NavbarLocaleChange/useNavbarLocale";

export type NavbarAccountView = "menu" | "language";

// The account menu of the phone sheet: the same entries as the desktop avatar dropdown in NavbarSettings.
// The language list opens as a view inside the sheet instead of a dropdown.
const view = defineModel<NavbarAccountView>("view", { default: "menu" });

const { logout } = useLogout();
const { t } = useI18n();
const TRANSLATION_KEY = "navbar.dropdown";

const { languageOptions, locale } = useNavbarLocale();
const currentLanguage = computed(() => languageOptions.value.find(option => option.value === locale.value)?.label);

function openApp(url: string) {
  window.open(url, "_blank", "noopener,noreferrer");
}
</script>

<template>
  <MobileSheetGroup v-if="view === 'language'">
    <MobileSheetRow
      v-for="option in languageOptions"
      :key="option.value"
      :label="option.label"
      @click="locale = option.value"
    >
      <template #icon>
        <img class="h-4 rounded-xs" :src="`/flags/${option.value.toUpperCase()}.svg`" alt="">
      </template>
      <template #trailing>
        <VIcon v-if="option.value === locale" name="check" class="text-primary-500" />
      </template>
    </MobileSheetRow>
  </MobileSheetGroup>

  <div v-else class="flex flex-col gap-4">
    <MobileSheetGroup>
      <MobileSheetRow
        icon="languages"
        :label="t(`${TRANSLATION_KEY}.change_language`)"
        chevron
        @click="view = 'language'"
      >
        <template #trailing>
          <span class="text-sm text-gray-400">{{ currentLanguage }}</span>
        </template>
      </MobileSheetRow>
      <MobileSheetRow
        icon="smartphone"
        :label="t('navbar.download_apps')"
      >
        <template #trailing>
          <span class="flex items-center gap-1">
            <span
              class="flex items-center justify-center w-9 h-9 rounded-full text-gray-500 active:bg-gray-200 dark:active:bg-dark-600"
              role="link"
              @click.stop="openApp('https://verbleif.com/android')"
            >
              <VIcon name="brand-android" />
            </span>
            <span
              class="flex items-center justify-center w-9 h-9 rounded-full text-gray-500 active:bg-gray-200 dark:active:bg-dark-600"
              role="link"
              @click.stop="openApp('https://verbleif.com/ios')"
            >
              <VIcon name="brand-apple" />
            </span>
          </span>
        </template>
      </MobileSheetRow>
    </MobileSheetGroup>

    <MobileSheetGroup>
      <MobileSheetRow
        variant="danger"
        icon="log-out"
        :label="t(`${TRANSLATION_KEY}.logout`)"
        @click="logout()"
      />
    </MobileSheetGroup>
  </div>
</template>
